// Sistema de monitoreo y logging estructurado
// Métricas: cold_start, map_ready, first_driver_marker, fetchDrivers, estimate

const makeBucket = () => ({
  count: 0,
  success: 0,
  failures: 0,
  timeouts: 0,
  cacheHits: 0,
  retries: 0,
  totalTime: 0,
  minTime: Infinity,
  maxTime: 0,
});

const metrics = {
  fetchDrivers: makeBucket(),
  estimate: makeBucket(),
  cold_start: makeBucket(),
  map_ready: makeBucket(),
  first_driver_marker: makeBucket(),
};

const PERF_TARGETS = {
  fetchDriversWifiMs: 800,
  fetchDrivers4gMs: 1500,
  locationReadsMax: 30,
  mapReadyMs: 3000,
  markersNormalMax: 40,
  markersSaverMax: 15,
};

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

let currentLogLevel = LOG_LEVELS.INFO;
let isProduction = process.env.NODE_ENV === 'production';

/** Epoch when JS bundle started evaluating / App mounted */
let coldStartAt = null;
let mapReadyLogged = false;
let firstDriverMarkerLogged = false;

export const setLogLevel = (level) => {
  currentLogLevel = LOG_LEVELS[level.toUpperCase()] || LOG_LEVELS.INFO;
};

export const setProductionMode = (production) => {
  isProduction = production;
};

const log = (level, message, data = {}) => {
  if (LOG_LEVELS[level] > currentLogLevel) return;

  const logEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    ...data,
  };

  if (isProduction) {
    console.log(JSON.stringify(logEntry));
  } else {
    const fn = console[level.toLowerCase()] || console.log;
    fn(`[${level}] ${message}`, data);
  }

  return logEntry;
};

export const logError = (message, error, data = {}) => {
  return log('ERROR', message, {
    ...data,
    error: {
      message: error?.message,
      code: error?.code,
      stack: isProduction ? undefined : error?.stack,
    },
  });
};

export const logWarn = (message, data = {}) => {
  return log('WARN', message, data);
};

export const logInfo = (message, data = {}) => {
  return log('INFO', message, data);
};

export const logDebug = (message, data = {}) => {
  return log('DEBUG', message, data);
};

export const markColdStart = () => {
  if (coldStartAt == null) {
    coldStartAt = Date.now();
    logInfo('cold_start marked', { at: coldStartAt });
  }
  return coldStartAt;
};

export const getColdStartAt = () => coldStartAt;

export const getStartupElapsed = () => {
  if (coldStartAt == null) return null;
  return Date.now() - coldStartAt;
};

/**
 * Record map interactive (MapView onMapReady). Once per session.
 */
export const markMapReady = (extra = {}) => {
  if (mapReadyLogged) return null;
  mapReadyLogged = true;
  const metric = startMetric('map_ready');
  if (coldStartAt != null) {
    metric.startTime = coldStartAt;
  }
  const duration = Date.now() - metric.startTime;
  const underTarget = duration < PERF_TARGETS.mapReadyMs;
  endMetric(metric, true, {
    ...extra,
    sinceColdStartMs: duration,
    targetMs: PERF_TARGETS.mapReadyMs,
    underTarget,
  });
  return duration;
};

/**
 * First driver marker painted. Once per session.
 */
export const markFirstDriverMarker = (extra = {}) => {
  if (firstDriverMarkerLogged) return null;
  firstDriverMarkerLogged = true;
  const metric = startMetric('first_driver_marker');
  if (coldStartAt != null) {
    metric.startTime = coldStartAt;
  }
  return endMetric(metric, true, {
    ...extra,
    sinceColdStartMs: Date.now() - (coldStartAt || Date.now()),
  });
};

export const getPerfTargets = () => ({ ...PERF_TARGETS });

export const startMetric = (operation) => {
  if (!metrics[operation]) {
    metrics[operation] = makeBucket();
  }
  return {
    operation,
    startTime: Date.now(),
  };
};

export const endMetric = (metric, success = true, metadata = {}) => {
  const duration = Date.now() - metric.startTime;
  const operation = metric.operation;

  if (!metrics[operation]) {
    metrics[operation] = makeBucket();
  }

  if (metrics[operation]) {
    metrics[operation].count++;
    if (success) {
      metrics[operation].success++;
    } else {
      metrics[operation].failures++;
    }

    metrics[operation].totalTime += duration;
    metrics[operation].minTime = Math.min(metrics[operation].minTime, duration);
    metrics[operation].maxTime = Math.max(metrics[operation].maxTime, duration);

    if (metadata.cacheHit) metrics[operation].cacheHits++;
    if (metadata.timeout) metrics[operation].timeouts++;
    if (metadata.retries) metrics[operation].retries += metadata.retries;
  }

  const logData = {
    operation,
    duration,
    success,
    ...metadata,
  };

  if (success) {
    logInfo(`${operation} completado`, logData);
  } else {
    logError(`${operation} falló`, new Error(metadata.error || 'Unknown error'), logData);
  }

  return {
    operation,
    duration,
    success,
    ...metadata,
  };
};

export const getMetrics = (operation) => {
  const metric = metrics[operation];
  if (!metric || metric.count === 0) {
    return null;
  }

  return {
    ...metric,
    avgTime: metric.totalTime / metric.count,
    successRate: (metric.success / metric.count) * 100,
    failureRate: (metric.failures / metric.count) * 100,
    cacheHitRate: metric.count > 0 ? (metric.cacheHits / metric.count) * 100 : 0,
  };
};

export const getAllMetrics = () => {
  return Object.keys(metrics).reduce((acc, key) => {
    const metric = getMetrics(key);
    if (metric) {
      acc[key] = metric;
    }
    return acc;
  }, {});
};

/**
 * Compare latest fetchDrivers sample against product targets.
 */
export const evaluateFetchDriversSample = (sample = {}) => {
  const duration = sample.duration;
  const locationReads = sample.locationReads;
  const targets = getPerfTargets();
  return {
    okDuration: duration == null || duration < targets.fetchDriversWifiMs,
    okReads: locationReads == null || locationReads <= targets.locationReadsMax,
    targets,
    sample,
  };
};

export const resetMetrics = () => {
  Object.keys(metrics).forEach((key) => {
    metrics[key] = makeBucket();
  });
  mapReadyLogged = false;
  firstDriverMarkerLogged = false;
};

export const withMetrics = (operation, fn) => {
  return async (...args) => {
    const metric = startMetric(operation);
    try {
      const result = await fn(...args);
      endMetric(metric, true);
      return result;
    } catch (error) {
      endMetric(metric, false, { error: error.message });
      throw error;
    }
  };
};
